/**
 * DocumentValidationPage Component
 * Document checklist and validation tables for extracted data.
 * Fetches real extraction results from the backend and renders tables
 * for each document type that was processed. Users can click on extracted
 * values to edit them inline; edits are saved to blob storage on Proceed.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FileText, ChevronDown, Loader2, Upload, AlertTriangle, AlertCircle, CheckCircle, Eye, CheckSquare, Plus, Trash2, RefreshCw } from 'lucide-react';
import { BRRApiClient } from '../services/api';
import { SessionAuditClient } from '../services/sessionApi';
import { SessionStatus } from '../services/sessionStatus';
import { ValidationPageData, ValidationTableRow, ValidationTableData, FieldHighlight, FieldSource, DocumentValidationData, DocumentCounts, ManualSectionEntry, TraceLinkHierarchyLevel, ProgressStep } from '../services/types';
import { useInspectionLotInfo } from '../hooks/useInspectionLotInfo';
import { useBRRFormData } from '../hooks/useBRRFormData';
import { extractCountryFromValidation, extractProductNameFromValidation, extractInspectionLotFromValidation, extractManufactureDate, extractExpiryDate } from '../utils/extractCountry';
import { formatDate } from '../utils/formatDate';
import { SaveStatusIndicator } from '../components/SaveStatusIndicator';
import { PdfHighlightModal } from '../components/PdfHighlightModal';
import { DeleteBRRButton } from '../components/DeleteBRRButton';
import { useValidatedState, ValidatedFormState } from '../contexts/ValidatedStateContext';

// Progress tracking step definitions - MUST be outside component to avoid re-creation on every render
const DOCUMENT_VALIDATION_LOAD_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing...' },
  { name: 'getSavedReview', label: 'Loading saved review...', weight: 2 },
  { name: 'getValidationData', label: 'Loading validation data...', weight: 3 },
  { name: 'mergeData', label: 'Merging data...' },
  { name: 'fetchTraceLink', label: 'Fetching TraceLink status...' },
  { name: 'complete', label: 'Finalizing...' },
];

const DOCUMENT_VALIDATION_SAVE_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing to save...' },
  { name: 'getExisting', label: 'Loading existing data...' },
  { name: 'mergeSnapshot', label: 'Merging table data...' },
  { name: 'saveToDb', label: 'Saving to database...', weight: 2 },
  { name: 'updateMetadata', label: 'Updating session metadata...' },
  { name: 'complete', label: 'Save complete' },
];

interface ValidationRow {
  fieldName: string;
  extractionKey: string;
  extractedValue: string;
  originalValue: string;
  validationSource1: { value: string; source: string } | null;
  validationSource2: { value: string; source: string } | null;
  validationSource3?: { value: string; source: string } | null;
  matchStatus?: 'match' | 'mismatch' | 'missing';
  highlight?: FieldHighlight[];
  ocrConfidence?: number;
}

interface DocumentValidationPageProps {
  batchId: string;
  sessionId: string;
  apiClient: BRRApiClient;
  sessionClient: SessionAuditClient;
  userEmail: string;
  productName?: string;
  selectedDocuments: string[];
  documentCounts?: DocumentCounts;
  batchType?: 'direct' | 'indirect';
  inspectionLotId?: string;
  selectedLotIds?: string[];
  brrLevel?: string;
  priority?: 'low' | 'medium' | 'high';
  expectedReleaseDate?: string | null;
  delayReason?: string | null;
  delayDetails?: string | null;
  delayActions?: string | null;
  delayCmoName?: string | null;
  inProgressAt?: string | null;
  onNext: (extractedCountry?: string, extractedProductName?: string, extractedInspectionLot?: string, extractedManufactureDate?: string | null, extractedExpiryDate?: string | null) => void;
  onBack: () => void;
  onDeleteBRR?: () => Promise<void>;
  blockNavigation?: (message: string) => void;
  unblockNavigation?: (error?: string | null) => void;
  isNavigationBlocked?: boolean;
  startProgressOperation?: (operationId: string, steps: ProgressStep[], title?: string) => void;
  advanceProgressStep?: (stepLabel?: string) => void;
  forceRefreshFromBlob?: boolean;
  onBlobRefreshComplete?: () => void;
  isActive?: boolean;
}

/**
 * Sanitize email to folder name — must match backend _sanitize_email_for_folder_name.
 */
function sanitizeEmailForFolderName(email: string): string {
  return email.replace('@', '_').replace(/[^a-zA-Z0-9._-]/g, '_');
}

/**
 * Mapping of legacy field names to current field names.
 * Used when loading saved BRRs that were created before field renames.
 */
const FIELD_NAME_MIGRATIONS: Record<string, string> = {
  'Packaging Quantity': 'Pack Size',
  'Cheplapharm Artworks Material Number': 'Artworks Material Number',
};

/**
 * Convert backend ValidationTableRow to frontend ValidationRow.
 */
function toValidationRows(backendRows: ValidationTableRow[]): ValidationRow[] {
  return backendRows.map(row => ({
    fieldName: FIELD_NAME_MIGRATIONS[row.field_name] || row.field_name,
    extractionKey: row.extraction_key,
    extractedValue: row.extracted_value || '',
    originalValue: row.original_value || '',
    validationSource1: row.validation_source_1
      ? { value: row.validation_source_1.value || '', source: row.validation_source_1.source }
      : null,
    validationSource2: row.validation_source_2
      ? { value: row.validation_source_2.value || '', source: row.validation_source_2.source }
      : null,
    validationSource3: row.validation_source_3
      ? { value: row.validation_source_3.value || '', source: row.validation_source_3.source }
      : null,
    matchStatus: row.match_status,
    highlight: row.highlight,
    ocrConfidence: row.ocr_confidence,
  }));
}

type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

function getConfidenceLevel(score?: number): ConfidenceLevel {
  if (score == null) return 'none';
  if (score >= 0.9) return 'high';
  if (score >= 0.6) return 'medium';
  return 'low';
}

const CONFIDENCE_STYLES: Record<ConfidenceLevel, { bg: string; text: string; dot: string; label: string }> = {
  high: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'High Confidence' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', label: 'Medium Confidence' },
  low: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: 'Low Confidence' },
  none: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'No Confidence Score Available' },
};

const OcrConfidenceBadge: React.FC<{ score?: number; size?: 'sm' | 'md' }> = ({ score, size = 'sm' }) => {
  const level = getConfidenceLevel(score);
  const s = CONFIDENCE_STYLES[level];

  if (size === 'md') {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
        <span className={`w-2 h-2 rounded-full ${s.dot}`} />
        {s.label}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

/** Base table keys in display order (multi-instance types use __SLOT__ placeholders) */
const BASE_TABLE_DISPLAY_ORDER = [
  'COC',
  'COA',
  '__DELIVERY_NOTE_SLOT__',
  '__BILL_OF_MATERIALS_SLOT__',
  '__TRANSPORT_REPORT_SLOT__',
  'PACKAGING_MATERIALS__LABEL',
  'PACKAGING_MATERIALS__FOIL',
  'PACKAGING_MATERIALS__LEAFLET',
  'PACKAGING_MATERIALS__FOLDING_BOX',
  'PACKAGING_MATERIALS__PRINTED_PACKAGING',
  'PACKAGING_MATERIALS__PACKAGING',
  '__BULK_COA_SLOT__',
  '__BULK_COC_SLOT__',
  '__DEVIATION_REPORT_SLOT__',
  '__OTHER_SLOT__',
];

/** Mapping from slot placeholders to their document type key and document ID */
const MULTI_INSTANCE_SLOTS: Record<string, { tableKey: string; docId: string }> = {
  '__DELIVERY_NOTE_SLOT__': { tableKey: 'DELIVERY_NOTE', docId: 'delivery' },
  '__BILL_OF_MATERIALS_SLOT__': { tableKey: 'BILL_OF_MATERIALS', docId: 'bom' },
  '__TRANSPORT_REPORT_SLOT__': { tableKey: 'TRANSPORT_REPORT', docId: 'transport' },
  '__BULK_COA_SLOT__': { tableKey: 'BULK_COA', docId: 'bulkCoa' },
  '__BULK_COC_SLOT__': { tableKey: 'BULK_COC', docId: 'bulkCoc' },
  '__DEVIATION_REPORT_SLOT__': { tableKey: 'DEVIATION_REPORT', docId: 'deviation' },
  '__OTHER_SLOT__': { tableKey: 'OTHER', docId: 'other' },
};

/**
 * Build the full table display order, expanding multi-instance slots into
 * indexed keys (e.g., BULK_COA_0, BULK_COA_1, ...).
 */
function buildTableDisplayOrder(docCounts: DocumentCounts): string[] {
  const result: string[] = [];
  for (const key of BASE_TABLE_DISPLAY_ORDER) {
    const slotConfig = MULTI_INSTANCE_SLOTS[key];
    if (slotConfig) {
      const count = docCounts[slotConfig.docId] || 0;
      // Always include at least one slot if the document type was selected
      // (count will be at least 1 if selected, 0 if not)
      for (let i = 0; i < count; i++) {
        result.push(`${slotConfig.tableKey}_${i}`);
      }
    } else {
      result.push(key);
    }
  }
  return result;
}

/** Which tables use ThreeSourceValidationTable (3 cross-validation columns) */
const THREE_SOURCE_TABLES = new Set(['COC', 'COA', 'DELIVERY_NOTE']);

/** Which tables show extraction only (no validation source columns) */
const EXTRACTION_ONLY_TABLES = new Set(['TRANSPORT_REPORT', 'OTHER']);

/** Resolve a display title for a table key, handling indexed multi-instance keys */
function getDocumentTypeTitle(tableKey: string, docCounts: DocumentCounts): string {
  // Check if this is an indexed multi-instance key (e.g., BULK_COA_0, DELIVERY_NOTE_1)
  const match = tableKey.match(/^(.+)_(\d+)$/);
  if (match) {
    const baseType = match[1];
    const idx = parseInt(match[2], 10);
    const baseTitle = DOCUMENT_TYPE_TITLES[baseType];
    if (baseTitle) {
      // Find the document ID for this base type to look up the count
      const slotEntry = Object.values(MULTI_INSTANCE_SLOTS).find(s => s.tableKey === baseType);
      const count = slotEntry ? (docCounts[slotEntry.docId] || 1) : 1;
      if (count > 1) {
        return `${baseTitle} (${idx + 1} of ${count})`;
      }
      return baseTitle;
    }
  }
  return DOCUMENT_TYPE_TITLES[tableKey] || tableKey;
}

/** Display titles for each document type */
const DOCUMENT_TYPE_TITLES: Record<string, string> = {
  'COC': 'Certificate of Conformance',
  'COA': 'Certificate of Analysis',
  'DELIVERY_NOTE': 'Delivery Note',
  'BILL_OF_MATERIALS': 'Bill of Materials',
  'TRANSPORT_REPORT': 'Transport Report',
  'PACKAGING_MATERIALS': 'Packaging Scans',
  'PACKAGING_MATERIALS__LABEL': 'Packaging Scan (Label)',
  'PACKAGING_MATERIALS__FOIL': 'Packaging Scan (Foil)',
  'PACKAGING_MATERIALS__LEAFLET': 'Packaging Scan (Leaflet)',
  'PACKAGING_MATERIALS__FOLDING_BOX': 'Packaging Scan (Folding Box)',
  'PACKAGING_MATERIALS__PRINTED_PACKAGING': 'Packaging Scan (Printed Packaging Materials)',
  'PACKAGING_MATERIALS__PACKAGING': 'Packaging Scan (Packaging)',
  'BULK_COA': 'Bulk Certificate of Analysis',
  'BULK_COC': 'Bulk Certificate of Conformance',
  'DEVIATION_REPORT': 'Deviation Report',
  'OTHER': 'Other Document',
};

/** Sentinel value for filename when no document has been uploaded */
const NO_DOCUMENT_FILENAME = 'No document uploaded';

/** Required document types that trigger a warning banner when deleted */
const REQUIRED_DOCUMENT_TYPES = new Set(['COA', 'COC', 'DELIVERY_NOTE', 'BILL_OF_MATERIALS']);

/** Map document IDs from StartBRRPage to table keys (base types for multi-instance) */
const DOCUMENT_ID_TO_TABLE_KEY: Record<string, string> = {
  'coc': 'COC',
  'coa': 'COA',
  'delivery': 'DELIVERY_NOTE',
  'bom': 'BILL_OF_MATERIALS',
  'transport': 'TRANSPORT_REPORT',
  'packagingLabel': 'PACKAGING_MATERIALS__LABEL',
  'packagingFoil': 'PACKAGING_MATERIALS__FOIL',
  'packagingLeaflet': 'PACKAGING_MATERIALS__LEAFLET',
  'packagingFoldingBox': 'PACKAGING_MATERIALS__FOLDING_BOX',
  'packagingPrintedPkg': 'PACKAGING_MATERIALS__PRINTED_PACKAGING',
  'packagingPackaging': 'PACKAGING_MATERIALS__PACKAGING',
  'bulkCoa': 'BULK_COA',
  'bulkCoc': 'BULK_COC',
  'deviation': 'DEVIATION_REPORT',
  'other': 'OTHER',
  // Note: serialization, aggregation don't have tables yet
  // Multi-instance types are expanded via indexed keys (e.g., BULK_COA_0, DELIVERY_NOTE_1)
};

/** Extraction keys that represent date fields — used for client-side format validation */
const DATE_FIELD_KEYS = new Set(['manufacturing_date', 'expiry_date', 'date_of_signature', 'document_date']);

/** Maps packaging sub-type table keys to TIBCO artwork materialGroupLabel values.
 *  Canonical source: brr_shared/models/document.py PACKAGING_SUBTYPE_TO_LABEL */
const PACKAGING_SUBTYPE_TO_ARTWORK_LABEL: Record<string, string> = {
  'PACKAGING_MATERIALS__LABEL': 'Labels',
  'PACKAGING_MATERIALS__FOIL': 'Foils',
  'PACKAGING_MATERIALS__LEAFLET': 'Leaflet',
  'PACKAGING_MATERIALS__FOLDING_BOX': 'Folding Box',
  'PACKAGING_MATERIALS__PRINTED_PACKAGING': 'Printed Packaging Materials',
  'PACKAGING_MATERIALS__PACKAGING': 'Packaging',
};

/** Check whether a string is a valid date (DD.MM.YYYY, MM.YYYY, YYYY, or empty).
 *  Only covers what a user can type — the backend normalizes to ISO before storing.
 *  Exported for unit testing. */
export const isValidDateValue = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;

  // DD.MM.YYYY
  const fullMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (fullMatch) {
    const [, dd, mm, yyyy] = fullMatch;
    const day = Number(dd), month = Number(mm), year = Number(yyyy);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return false;
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
  }

  // MM.YYYY
  const yearMonthMatch = trimmed.match(/^(\d{2})\.(\d{4})$/);
  if (yearMonthMatch) {
    const [, mm, yyyy] = yearMonthMatch;
    const month = Number(mm), year = Number(yyyy);
    return month >= 1 && month <= 12 && year >= 1900 && year <= 2100;
  }

  // YYYY
  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = Number(trimmed);
    return year >= 1900 && year <= 2100;
  }

  return false;
};

/** Generate empty placeholder rows for a document type when no extraction exists.
 *
 * IMPORTANT: Field definitions must stay synchronized with backend schema in:
 * src/functions/brr_shared/document_validation.py (_build_*_fields functions)
 *
 * All document types use static schemas matching their backend field definitions.
 *
 * Any changes to field names or extraction keys in the backend require
 * corresponding updates here. Run unit tests to verify schema consistency.
 *
 * Exported for unit testing.
 */
export const generateEmptyRows = (documentType: string): ValidationRow[] => {
  // Define the expected fields for each document type, matching backend schema exactly
  const fieldMaps: Record<string, Array<{ fieldName: string; extractionKey: string }>> = {
    'COC': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'Bulk Batch ID', extractionKey: 'cmo_bulk_batch_id' },
      { fieldName: 'CMO Finished Product Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Pack Size', extractionKey: 'package_size_and_type' },
      { fieldName: 'Marketing Auth Number', extractionKey: 'marketing_authorisation_number' },
      { fieldName: 'Destination Country', extractionKey: 'destination_country' },
      { fieldName: 'Dosage Form', extractionKey: 'dosage_form' },
      { fieldName: 'Strength / Potency', extractionKey: 'strength' },
      { fieldName: 'Lot Quantity', extractionKey: 'quantity' },
      { fieldName: 'Manufacturing Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Release Text', extractionKey: 'release_text' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
      { fieldName: 'Date of Signature', extractionKey: 'date_of_signature' },
      { fieldName: 'Deviations Referenced', extractionKey: 'deviations_referenced' },
    ],
    'COA': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'Finished Product Batch Quantity', extractionKey: 'fp_batch_quantity' },
      { fieldName: 'CMO Bulk Batch ID', extractionKey: 'cmo_bulk_batch_id' },
      { fieldName: 'CMO Finished Product Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Manufacture Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
      { fieldName: 'Deviations Referenced', extractionKey: 'deviations_referenced' },
    ],
    'DELIVERY_NOTE': [
      { fieldName: 'CMO Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Batch Quantity', extractionKey: 'quantity' },
      { fieldName: 'Manufacturing Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Destination Address', extractionKey: 'destination_address' },
      { fieldName: 'Destination Country', extractionKey: 'destination_country' },
    ],
    'BILL_OF_MATERIALS': [
      { fieldName: 'Artworks Material Number', extractionKey: 'artworks.0.cp_artworks_material_number' },
      { fieldName: 'CMO Artworks Material Number', extractionKey: 'artworks.0.cmo_artworks_material_number' },
      { fieldName: 'Description', extractionKey: 'artworks.0.description' },
    ],
    'TRANSPORT_REPORT': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'CMO Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Quantity', extractionKey: 'quantity' },
      { fieldName: 'SAP Material Number', extractionKey: 'sap_material_number' },
      { fieldName: 'Evaluation of Transport', extractionKey: 'evaluation_of_transport' },
      { fieldName: 'Storage Location', extractionKey: 'storage_location' },
      { fieldName: 'Signatures', extractionKey: 'signatures' },
      { fieldName: 'Date of Signature', extractionKey: 'date_of_signature' },
    ],
    'PACKAGING_MATERIALS': [
      { fieldName: 'Artworks Material Number', extractionKey: 'kau_material_number' },
      { fieldName: 'Artworks Version', extractionKey: 'kau_version' },
      { fieldName: 'CMO Artworks Material Number', extractionKey: 'cmo_material_number' },
      { fieldName: 'Packaging Type', extractionKey: 'packaging_type' },
    ],
    'BULK_COA': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'Finished Product Batch Quantity', extractionKey: 'fp_batch_quantity' },
      { fieldName: 'CMO Bulk Batch ID', extractionKey: 'cmo_bulk_batch_id' },
      { fieldName: 'Manufacture Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
      { fieldName: 'Deviations Referenced', extractionKey: 'deviations_referenced' },
    ],
    'BULK_COC': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'Bulk Batch ID', extractionKey: 'cmo_bulk_batch_id' },
      { fieldName: 'Dosage Form', extractionKey: 'dosage_form' },
      { fieldName: 'Strength / Potency', extractionKey: 'strength' },
      { fieldName: 'Lot Quantity', extractionKey: 'quantity' },
      { fieldName: 'Manufacturing Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Release Text', extractionKey: 'release_text' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
      { fieldName: 'Deviations Referenced', extractionKey: 'deviations_referenced' },
    ],
    'DEVIATION_REPORT': [
      { fieldName: 'Deviation Number', extractionKey: 'deviation_number' },
      { fieldName: 'Version', extractionKey: 'deviation_version' },
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'CMO Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Quantity', extractionKey: 'quantity' },
      { fieldName: 'Manufacturing Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
    ],
    'OTHER': [
      { fieldName: 'Product Name', extractionKey: 'product_name' },
      { fieldName: 'CMO Batch ID', extractionKey: 'cmo_fp_batch_id' },
      { fieldName: 'Quantity', extractionKey: 'quantity' },
      { fieldName: 'Manufacturing Date', extractionKey: 'manufacturing_date' },
      { fieldName: 'Expiry Date', extractionKey: 'expiry_date' },
      { fieldName: 'Signature', extractionKey: 'document_signature_present' },
    ],
  };

  // Indexed multi-instance keys (PACKAGING_MATERIALS_0, BILL_OF_MATERIALS_0, etc.) use the base schema
  const resolvedType = documentType.replace(/__[A-Z_]+$/, '').replace(/_\d+$/, '');
  const fields = fieldMaps[resolvedType];

  // For undefined doc types, return empty array
  if (!fields) {
    return [];
  }

  return fields.map(field => ({
    fieldName: field.fieldName,
    extractionKey: field.extractionKey,
    extractedValue: '',
    originalValue: '',
    validationSource1: null,
    validationSource2: null,
    validationSource3: null,
    matchStatus: 'missing' as const,
  }));
};

/** Render transport type badge for destination country field */
const TransportTypeBadge: React.FC<{ batchType?: 'direct' | 'indirect' }> = ({ batchType }) => {
  if (!batchType) return null;

  const isDirect = batchType === 'direct';
  const bgColor = isDirect ? 'bg-green-100' : 'bg-blue-100';
  const textColor = isDirect ? 'text-green-800' : 'text-blue-800';
  const label = isDirect ? 'Direct' : 'Indirect';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} ml-2`}>
      {label}
    </span>
  );
};

/** Editable extracted value cell — extracted to module scope to avoid remounting on parent re-renders */
const EditableValueCell: React.FC<{
  tableKey: string;
  extractionKey: string;
  originalExtracted: string;
  isEditing: boolean;
  displayValue: string;
  isOverridden: boolean;
  onStartEdit: () => void;
  onEdit: (newValue: string) => void;
  onStopEdit: () => void;
  className?: string;
  cellWidth?: string;
  hasError?: boolean;
}> = ({ tableKey, extractionKey, isEditing, displayValue, isOverridden, onStartEdit, onEdit, onStopEdit, className, cellWidth, hasError }) => {
  const id = `${tableKey}:${extractionKey}`;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const cellStyle = {
    width: cellWidth || '25%',
    whiteSpace: 'normal' as const,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    wordBreak: 'break-word' as const,
    maxWidth: cellWidth || '25%',
    minWidth: '0'
  };

  if (isEditing) {
    return (
      <td className={className || 'px-6 py-4'} style={cellStyle}>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => onEdit(e.target.value)}
          onBlur={onStopEdit}
          onKeyDown={(e) => { if (e.key === 'Enter') onStopEdit(); }}
          className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-500' : 'border-green-400 focus:ring-green-500'}`}
          data-testid={`edit-input-${id}`}
        />
      </td>
    );
  }

  return (
    <td className={className || 'px-6 py-4'} style={cellStyle}>
      <div
        onClick={onStartEdit}
        className={`text-sm italic cursor-pointer rounded px-1 py-0.5 -mx-1 ${hasError ? 'text-red-600 bg-red-50 border border-red-300 hover:bg-red-100' : 'text-gray-500 hover:bg-green-50'}`}
        style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}
        data-testid={`editable-value-${id}`}
      >
        {displayValue || '\u2014'}
        {isOverridden && !hasError && (
          <span className="ml-2 text-xs text-green-600 font-medium whitespace-nowrap" data-testid={`edited-badge-${id}`}>(edited)</span>
        )}
      </div>
      {hasError && (
        <p className="text-xs text-red-500 mt-1" data-testid={`date-error-${id}`}>Expected format: DD.MM.YYYY, MM.YYYY, or YYYY</p>
      )}
    </td>
  );
};

const DocumentValidationPageComponent: React.FC<DocumentValidationPageProps> = ({
  batchId,
  sessionId,
  apiClient,
  sessionClient,
  userEmail,
  productName,
  selectedDocuments,
  documentCounts = {},
  batchType,
  inspectionLotId,
  selectedLotIds,
  brrLevel,
  priority,
  expectedReleaseDate,
  delayReason,
  delayDetails,
  delayActions,
  delayCmoName,
  inProgressAt,
  onNext,
  onBack,
  onDeleteBRR,
  blockNavigation,
  unblockNavigation,
  isNavigationBlocked,
  startProgressOperation,
  advanceProgressStep,
  forceRefreshFromBlob = false,
  onBlobRefreshComplete,
  isActive
}) => {
  // API data state
  const [validationData, setValidationData] = useState<ValidationPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [noResults, setNoResults] = useState(false);
  // Documents uploaded but nothing extracted — user enters the data by hand instead of being stuck
  const [extractionFailed, setExtractionFailed] = useState(false);

  // State to track which tables are expanded
  const [expandedTables, setExpandedTables] = useState<string[]>([]);

  const toggleTable = (tableId: string) => {
    setExpandedTables(prev =>
      prev.includes(tableId)
        ? prev.filter(id => id !== tableId)
        : [...prev, tableId]
    );
  };

  // State to track confirmation checkbox
  const [fieldsConfirmed, setFieldsConfirmed] = useState(false);

  // State to track serialization requirement selection
  const [serializationRequirement, setSerializationRequirement] = useState<string>('');

  // State to track aggregation requirement selection
  const [aggregationRequirement, setAggregationRequirement] = useState<string>('');

  // TraceLink lot status state
  const [traceLinkData, setTraceLinkData] = useState<Record<string, string> | null>(null);
  const [traceLinkFound, setTraceLinkFound] = useState<boolean | null>(null);
  const [traceLinkNotFoundMessage, setTraceLinkNotFoundMessage] = useState<string | null>(null);
  const [traceLinkLoading, setTraceLinkLoading] = useState(false);
  const [traceLinkError, setTraceLinkError] = useState<string | null>(null);

  // TraceLink serial number hierarchy level state
  const [hierarchyLevels, setHierarchyLevels] = useState<TraceLinkHierarchyLevel[] | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState<string | null>(null);

  // Inline editing state
  const [overrides, setOverrides] = useState<Record<string, Record<string, string>>>({});
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [invalidDateFields, setInvalidDateFields] = useState<Set<string>>(new Set());

  // Manual document sections state
  const [manualSections, setManualSections] = useState<ManualSectionEntry[]>([]);
  const [showAddDocDropdown, setShowAddDocDropdown] = useState(false);
  const [syncedData, setSyncedData] = useState<Record<string, any>>({});
  const [syncingSection, setSyncingSection] = useState<string | null>(null);

  // Fetch artwork data from inspection lot info for packaging material validation sources
  const { data: lotInfoData } = useInspectionLotInfo({
    apiClient,
    inspectionLotId: inspectionLotId ?? null,
    brrLevel: 'na',
  });

  // Fetch per-lot SAP quantities when multiple lots are selected
  const hasMultipleLots = selectedLotIds && selectedLotIds.length > 1;
  const { data: formData } = useBRRFormData({
    inspectionLotId: inspectionLotId ?? null,
    productName,
    sessionId,
    selectedLotIds,
    apiClient,
    autoLoad: !!hasMultipleLots,
  });
  const sapLotQuantities = formData?.sapIdentityBatch?.sapLotQuantities;

  // Deleted documents state - tracks table keys that have been deleted by user
  const [deletedDocuments, setDeletedDocuments] = useState<Set<string>>(new Set());
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);

  // Highlight modal state
  const [highlightModal, setHighlightModal] = useState<{
    blobName: string;
    highlightPage: number;
    highlightRects: import('../services/types').FieldHighlightRect[];
    matchType: string;
    fieldName: string;
    matchedText: string;
  } | null>(null);

  // Save/load state
  const [saveError, setSaveError] = useState<string>('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loadingState, setLoadingState] = useState(false);

  // Track initial state for unsaved changes detection
  const initialStateRef = useRef<{
    overrides: Record<string, Record<string, string>>;
    fieldsConfirmed: boolean;
    serializationRequirement: string;
    aggregationRequirement: string;
  } | null>(null);

  // Track previous sessionId to detect session changes
  const previousSessionIdRef = useRef<string | null>(null);

  // Compute if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialStateRef.current) return false;
    const initial = initialStateRef.current;

    // Compare overrides
    const currentOverridesJson = JSON.stringify(overrides);
    const initialOverridesJson = JSON.stringify(initial.overrides);
    if (currentOverridesJson !== initialOverridesJson) return true;

    // Compare other fields
    if (fieldsConfirmed !== initial.fieldsConfirmed) return true;
    if (serializationRequirement !== initial.serializationRequirement) return true;
    if (aggregationRequirement !== initial.aggregationRequirement) return true;

    return false;
  }, [overrides, fieldsConfirmed, serializationRequirement, aggregationRequirement]);

  // Validated state context for persisting data across navigation
  const { validatedState, updateFields, saveValidatedState } = useValidatedState();

  const cellId = (tableKey: string, extractionKey: string) => `${tableKey}:${extractionKey}`;

  // Extract base type from indexed key (e.g., DELIVERY_NOTE_0 -> DELIVERY_NOTE)
  const getBaseType = (tableKey: string): string => {
    const match = tableKey.match(/^(.+)_(\d+)$/);
    return match ? match[1] : tableKey;
  };

  // Get override value, checking both indexed key and base type for backward compatibility
  const getOverrideValue = (tableKey: string, extractionKey: string): string | undefined => {
    const override = overrides[tableKey]?.[extractionKey];
    if (override !== undefined) return override;
    // Fall back to base type for backward compatibility with validated state
    const baseType = getBaseType(tableKey);
    return baseType !== tableKey ? overrides[baseType]?.[extractionKey] : undefined;
  };

  const getDisplayValue = (tableKey: string, extractionKey: string, originalExtracted: string): string =>
    getOverrideValue(tableKey, extractionKey) ?? originalExtracted;

  const isOverridden = (tableKey: string, extractionKey: string): boolean =>
    getOverrideValue(tableKey, extractionKey) !== undefined;

  // Validates on change, not only on blur: filling a date by clicking a source column never blurs,
  // so an invalid date used to slip past the Continue gate.
  const handleEdit = (tableKey: string, extractionKey: string, newValue: string) => {
    const trimmed = newValue.trim();
    setOverrides(prev => ({
      ...prev,
      [tableKey]: { ...(prev[tableKey] || {}), [extractionKey]: trimmed }
    }));

    if (DATE_FIELD_KEYS.has(extractionKey)) {
      const id = cellId(tableKey, extractionKey);
      setInvalidDateFields(prev => {
        const next = new Set(prev);
        if (trimmed && !isValidDateValue(trimmed)) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    }
  };

  const handleStopEdit = (tableKey: string, extractionKey: string) => {
    if (DATE_FIELD_KEYS.has(extractionKey)) {
      const id = cellId(tableKey, extractionKey);
      const value = overrides[tableKey]?.[extractionKey] ?? '';
      setInvalidDateFields(prev => {
        const next = new Set(prev);
        if (value && !isValidDateValue(value)) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    }
    setEditingCell(null);
  };

  const hasOverrides = Object.keys(overrides).some(key => Object.keys(overrides[key]).length > 0);

  // Handle document deletion - removes blob from storage and hides table.
  // For combined documents, deletes all sibling tables sharing the same source file.
  const handleDeleteDocument = useCallback(async (tableKey: string, blobName: string | undefined) => {
    setDeletingDocument(tableKey);
    try {
      // Delete the blob file if it exists
      if (blobName) {
        await apiClient.deleteBlobDocument(blobName);
      }

      // Find all sibling table keys that share the same source_file (combined documents)
      const sourceFile = validationData?.tables[tableKey]?.source_file;
      const keysToDelete: string[] = [tableKey];
      if (sourceFile && validationData?.tables) {
        for (const [key, tableData] of Object.entries(validationData.tables)) {
          if (key !== tableKey && tableData && tableData.source_file === sourceFile) {
            keysToDelete.push(key);
          }
        }
      }

      // Mark all sibling documents as deleted
      setDeletedDocuments(prev => {
        const next = new Set([...prev]);
        for (const key of keysToDelete) next.add(key);
        return next;
      });

      // Clear overrides for all siblings
      setOverrides(prev => {
        const next = { ...prev };
        for (const key of keysToDelete) delete next[key];
        return next;
      });

      // Clear from expanded tables
      setExpandedTables(prev => prev.filter(id => !keysToDelete.includes(id)));
    } catch (error) {
      console.error('[DocumentValidation] Failed to delete document:', error);
      setSaveError(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeletingDocument(null);
    }
  }, [apiClient, validationData]);

  // Compute missing required documents (those that were deleted or never uploaded)
  const missingRequiredDocuments = useMemo(() => {
    const missing: string[] = [];
    for (const docType of REQUIRED_DOCUMENT_TYPES) {
      // Check if it's a multi-instance type
      const slotEntry = Object.values(MULTI_INSTANCE_SLOTS).find(s => s.tableKey === docType);
      if (slotEntry) {
        // For multi-instance types, check all indexed keys
        const count = documentCounts[slotEntry.docId] || 0;
        let allDeleted = count > 0;
        for (let i = 0; i < count; i++) {
          const indexedKey = `${docType}_${i}`;
          if (!deletedDocuments.has(indexedKey)) {
            allDeleted = false;
            break;
          }
        }
        if (allDeleted && count > 0) {
          missing.push(docType);
        }
      } else {
        // Single-instance type
        if (deletedDocuments.has(docType) && selectedDocuments.includes(
          Object.entries(DOCUMENT_ID_TO_TABLE_KEY).find(([, v]) => v === docType)?.[0] || ''
        )) {
          missing.push(docType);
        }
      }
    }
    return missing;
  }, [deletedDocuments, documentCounts, selectedDocuments]);

  // Rebuild local overrides from persisted validated state so the edited badge
  // is shown correctly when the user navigates back to this page.
  const restoreOverridesFromValidatedState = useCallback((state: ValidatedFormState | null): Record<string, Record<string, string>> => {
    if (!state?.fields) return {};

    const restored: Record<string, Record<string, string>> = {};

    Object.entries(state.fields).forEach(([fieldPath, fieldValue]) => {
      // Only restore fields that were manually edited (override: true)
      if (!fieldValue.override) return;

      const parts = fieldPath.split('.');
      if (parts.length !== 2) {
        console.warn(`[DocumentValidation] Invalid field path format: ${fieldPath}`);
        return;
      }

      const [tableKey, extractionKey] = parts;
      if (!restored[tableKey]) restored[tableKey] = {};
      // Convert boolean to string to match existing overrides format
      restored[tableKey][extractionKey] = String(fieldValue.value);
    });

    return restored;
  }, []);

  // Re-apply overrides from validated state when navigating back to this page
  useEffect(() => {
    if (validatedState) {
      const restoredOverrides = restoreOverridesFromValidatedState(validatedState);
      if (Object.keys(restoredOverrides).length > 0) {
        setOverrides(restoredOverrides);
      }
    }
  }, [validatedState, restoreOverridesFromValidatedState]);

  // Save progress to Neo4j
  const handleSaveProgress = useCallback(async (): Promise<boolean> => {
    startProgressOperation?.('documentValidation_save', DOCUMENT_VALIDATION_SAVE_STEPS, 'Saving Document Validation');
    advanceProgressStep?.('Preparing to save...');
    blockNavigation?.('Saving Document Validation...');
    setSaving(true);
    setSaveError('');

    try {
      // Parse batchId to number safely
      const numericBatchId = parseInt(batchId, 10);
      if (isNaN(numericBatchId)) {
        throw new Error(`Invalid batch ID: ${batchId}`);
      }

      advanceProgressStep?.('Loading existing data...');
      // Build page data including full tables snapshot for reload without blob storage
      // CRITICAL: Before saving, fetch existing Neo4j data and merge to prevent data loss.
      // This ensures we never overwrite valid table data (with filenames) with null values
      // from a new extraction that only processed new documents.
      let mergedTablesSnapshot = validationData;
      try {
        const existingReview = await apiClient.getSavedReview(batchId, sessionId);
        const existingSnapshot = existingReview?.documentValidation?.tablesSnapshot;
        if (existingSnapshot?.tables && validationData?.tables) {
          // Merge: preserve existing non-null tables, overlay current non-null tables
          const mergedTables: Record<string, unknown> = {};

          // Start with existing tables (preserves old data)
          for (const [key, value] of Object.entries(existingSnapshot.tables)) {
            if (value !== null) {
              mergedTables[key] = value;
            }
          }

          // Overlay current tables (only non-null values)
          for (const [key, value] of Object.entries(validationData.tables)) {
            if (value !== null) {
              mergedTables[key] = value;
            }
          }

          mergedTablesSnapshot = {
            ...validationData,
            tables: mergedTables as Record<string, ValidationTableData | null>,
            // Update documentsFound to reflect actual non-null tables
            documents_found: Object.values(mergedTables).filter(v => v !== null).length,
          };
        }
      } catch (fetchError) {
        console.warn('[DocumentValidation] Could not fetch existing data for merge, using current data:', fetchError);
      }

      advanceProgressStep?.('Merging table data...');
      const pageData: DocumentValidationData = {
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
        fieldsConfirmed,
        serializationRequirement: serializationRequirement || undefined,
        aggregationRequirement: aggregationRequirement || undefined,
        manualSections: manualSections.length > 0 ? manualSections : undefined,
        syncedResults: Object.keys(syncedData).length > 0 ? syncedData : undefined,
        tablesSnapshot: mergedTablesSnapshot ?? undefined,
        deletedDocuments: deletedDocuments.size > 0 ? Array.from(deletedDocuments) : undefined,
        // TraceLink lot status data for Word export
        tracelinkGtin: traceLinkData?.['GTIN'] || undefined,
        tracelinkQuantity: traceLinkData?.['Quantity'] || undefined,
        tracelinkHierarchyLevels: hierarchyLevels || undefined,
      };

      advanceProgressStep?.('Saving to database...');
      // Primary save: Neo4j
      const response = await apiClient.saveReview({
        batchId: batchId,
        sessionId,
        pageName: 'document_validation',
        pageData,
        batchType,
        status: 'draft',
      });

      // Handle backend validation warnings
      if (response.warnings && response.warnings.length > 0) {
        console.warn('[DocumentValidation] Backend validation warnings:', response.warnings);
        setSaveError(`Warning: ${response.warnings.join('; ')}`);
      }

      // Update initial state to current (no more unsaved changes)
      initialStateRef.current = {
        overrides: JSON.parse(JSON.stringify(overrides)),
        fieldsConfirmed,
        serializationRequirement,
        aggregationRequirement,
      };

      advanceProgressStep?.('Updating session metadata...');
      // IMPORTANT: Include details JSON with selectedDocuments so that when the user
      // resumes this session, the correct document types are shown.
      try {
        await sessionClient.updateSession(sessionId, {
          batchId: batchId,
          currentPage: 'document-validation',
          lastSavedAt: new Date(),
          status: SessionStatus.InProgress,
          details: JSON.stringify({
            inspectionLotId: inspectionLotId ?? null,
            productName: productName ?? null,
            batchType: batchType ?? 'direct',
            priority: priority ?? 'medium',
            selectedDocuments: selectedDocuments,
            brrLevel: brrLevel ?? 'na',
            documentCounts: documentCounts,
            expectedReleaseDate: expectedReleaseDate ?? null,
            delayReason: delayReason ?? null,
            delayDetails: delayDetails ?? null,
            delayActions: delayActions ?? null,
            delayCmoName: delayCmoName ?? null,
            inProgressAt: inProgressAt ?? null,
          }),
        });
      } catch (sessionError) {
        console.error('[DocumentValidation] session updateSession FAILED:', sessionError);
        if (!response.warnings || response.warnings.length === 0) {
          setSaveError('Warning: Session metadata save failed, but progress is saved.');
        }
      }

      advanceProgressStep?.('Save complete');
      setLastSaved(new Date());
      unblockNavigation?.();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[DocumentValidation] Save failed:', error);
      setSaveError(`Failed to save progress: ${errorMessage}`);
      unblockNavigation?.(`Save failed: ${errorMessage}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [batchId, sessionId, overrides, fieldsConfirmed, serializationRequirement, aggregationRequirement, batchType, apiClient, sessionClient, inspectionLotId, productName, priority, selectedDocuments, brrLevel, documentCounts, manualSections, syncedData, validationData, deletedDocuments, traceLinkData, hierarchyLevels, blockNavigation, unblockNavigation, startProgressOperation, advanceProgressStep]);

  // Warn user about unsaved changes when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(async () => {
    if (hasUnsavedChanges()) {
      const shouldSave = window.confirm(
        'You have unsaved changes. Do you want to save before leaving?\n\nClick OK to save, or Cancel to discard changes.'
      );
      if (shouldSave) {
        const saved = await handleSaveProgress();
        if (!saved) {
          // Save failed, ask if they want to leave anyway
          const leaveAnyway = window.confirm('Save failed. Do you want to leave anyway and discard your changes?');
          if (!leaveAnyway) return;
        }
      }
    }
    onBack();
  }, [hasUnsavedChanges, handleSaveProgress, onBack]);

  // Load saved state and fetch validation data on mount — single effect that calls
  // getSavedReview once and runs it in parallel with getValidationData via Promise.all
  useEffect(() => {
    let cancelled = false;

    const loadAndFetchData = async () => {
      // Clear form state when session changes (must happen synchronously before fetch)
      const sessionHasChanged =
        previousSessionIdRef.current &&
        previousSessionIdRef.current !== sessionId;

      if (sessionHasChanged) {
        setOverrides({});
        setFieldsConfirmed(false);
        setSerializationRequirement('');
        setAggregationRequirement('');
        setInvalidDateFields(new Set());
        setSaveError('');
        setLastSaved(null);
        setDeletedDocuments(new Set());
        setExtractionFailed(false);
      }

      previousSessionIdRef.current = sessionId;

      if (!sessionId || !batchId) {
        setNoResults(true);
        setLoading(false);
        unblockNavigation?.();
        return;
      }

      setLoading(true);
      setLoadingState(true);
      setNoResults(false);

      startProgressOperation?.('documentValidation_load', DOCUMENT_VALIDATION_LOAD_STEPS, 'Loading Document Validation');
      advanceProgressStep?.('Preparing...');
      blockNavigation?.('Loading Document Validation...');

      try {
        // Fetch saved review and blob data in parallel — single getSavedReview call
        advanceProgressStep?.('Loading saved review...');
        const [savedReview, blobData] = await Promise.all([
          apiClient.getSavedReview(batchId, sessionId),
          apiClient.getValidationData(sessionId),
        ]);

        if (cancelled) return;

        // --- Restore form state from saved review ---

        if (savedReview && savedReview.documentValidation) {
          const pageData = savedReview.documentValidation;

          if (pageData.overrides !== undefined) {
            setOverrides(pageData.overrides);
          }
          if (pageData.fieldsConfirmed !== undefined) {
            setFieldsConfirmed(pageData.fieldsConfirmed);
          }
          if (pageData.serializationRequirement !== undefined) {
            setSerializationRequirement(pageData.serializationRequirement);
          }
          if (pageData.aggregationRequirement !== undefined) {
            setAggregationRequirement(pageData.aggregationRequirement);
          }
          if (pageData.manualSections) {
            setManualSections(pageData.manualSections);
          }
          if (pageData.syncedResults) {
            setSyncedData(pageData.syncedResults);
          }
          if (pageData.deletedDocuments && pageData.deletedDocuments.length > 0) {
            setDeletedDocuments(new Set(pageData.deletedDocuments));
          }

          initialStateRef.current = {
            overrides: pageData.overrides ? JSON.parse(JSON.stringify(pageData.overrides)) : {},
            fieldsConfirmed: pageData.fieldsConfirmed ?? false,
            serializationRequirement: pageData.serializationRequirement ?? '',
            aggregationRequirement: pageData.aggregationRequirement ?? '',
          };
        } else {
          initialStateRef.current = {
            overrides: {},
            fieldsConfirmed: false,
            serializationRequirement: '',
            aggregationRequirement: '',
          };
        }

        // --- Merge Neo4j snapshot with blob data ---
        advanceProgressStep?.('Loading validation data...');
        const isClonedSession = !!savedReview?.clonedFromSessionId;
        const existingSnapshot = savedReview?.documentValidation?.tablesSnapshot;
        const hasTablesSnapshot = !!existingSnapshot?.tables;

        advanceProgressStep?.('Merging data...');

        if (blobData === null) {
          // No blob data - use Neo4j snapshot if available
          if (hasTablesSnapshot) {
            setValidationData(existingSnapshot!);
            setExtractionFailed(false);
            setExpandedTables([...buildTableDisplayOrder(documentCounts), 'serialization', 'aggregation']);
          } else if (selectedDocuments.length > 0) {
            setValidationData({ status: 'extraction_failed', documents_found: 0, tables: {} });
            setExtractionFailed(true);
            setNoResults(false);
            setExpandedTables(['serialization', 'aggregation']);
          } else {
            if (isClonedSession) {
              console.error('[DocumentValidation] Cloned BRR has no tablesSnapshot and no blob data');
            }
            setNoResults(true);
            setExtractionFailed(false);
          }
        } else {
          // Blob has data - merge with Neo4j snapshot if available

          if (hasTablesSnapshot) {
            const existingKeys = new Set(Object.keys(existingSnapshot!.tables));
            const newBlobTables = Object.fromEntries(
              Object.entries(blobData.tables).filter(([key, v]) => {
                if (v === null) return false;
                const blobEntry = v as { filename?: string };
                const isNewDocument = !existingKeys.has(key);
                const hasFilename = !!blobEntry.filename;
                return isNewDocument || hasFilename;
              })
            );
            const mergedTables = {
              ...existingSnapshot!.tables,
              ...newBlobTables,
            };
            const mergedData: ValidationPageData = {
              ...blobData,
              tables: mergedTables,
              documents_found: Object.keys(mergedTables).filter(k => mergedTables[k] !== null).length,
            };
            setValidationData(mergedData);
          } else {
            setValidationData(blobData);
          }
          setExtractionFailed(false);
          setExpandedTables([...buildTableDisplayOrder(documentCounts), 'serialization', 'aggregation']);
        }

        onBlobRefreshComplete?.();
      } catch (error) {
        console.error('[DocumentValidation] Failed to load data:', error);
        if (!cancelled) {
          setNoResults(true);
          setExtractionFailed(false);
          initialStateRef.current = {
            overrides: {},
            fieldsConfirmed: false,
            serializationRequirement: '',
            aggregationRequirement: '',
          };
        }
      } finally {
        advanceProgressStep?.('Finalizing...');
        if (!cancelled) {
          setLoadingState(false);
          setLoading(false);
          unblockNavigation?.();
        }
      }
    };

    loadAndFetchData();

    return () => { cancelled = true; };
  }, [sessionId, batchId, apiClient, documentCounts, selectedDocuments, forceRefreshFromBlob, onBlobRefreshComplete, blockNavigation, unblockNavigation, startProgressOperation, advanceProgressStep]);

  // Fetch TraceLink lot status when materialNumber and vendorBatchId are available
  useEffect(() => {
    if (!validationData?.materialNumber || !validationData?.vendorBatchId) return;
    // Skip fetch if we already have a result (found or not found)
    if (traceLinkFound !== null) return;
    let cancelled = false;

    const fetchTraceLinkData = async () => {
      setTraceLinkLoading(true);
      setTraceLinkError(null);
      setTraceLinkFound(null);
      setTraceLinkNotFoundMessage(null);
      try {
        const result = await apiClient.getTraceLinkLotStatus(
          validationData.materialNumber!,
          validationData.vendorBatchId!
        );
        if (cancelled) return;
        if (result.found && result.lotStatus) {
          setTraceLinkFound(true);
          setTraceLinkData(result.lotStatus);
        } else {
          setTraceLinkFound(false);
          setTraceLinkNotFoundMessage(
            result.message || 'This material number and vendor batch ID combination was not found in TraceLink.'
          );
        }
      } catch (err) {
        if (cancelled) return;
        let message = 'Failed to fetch TraceLink data';
        if (err instanceof Error) {
          // Try to extract a readable message from API error JSON
          const jsonMatch = err.message.match(/:\s*(\{.*\})\s*$/);
          if (jsonMatch) {
            try {
              const parsed = JSON.parse(jsonMatch[1]);
              message = parsed.error || parsed.message || err.message;
            } catch {
              message = err.message;
            }
          } else {
            message = err.message;
          }
        }
        setTraceLinkError(message);
      } finally {
        if (!cancelled) setTraceLinkLoading(false);
      }
    };

    fetchTraceLinkData();
    return () => { cancelled = true; };
  }, [validationData?.materialNumber, validationData?.vendorBatchId, apiClient]);

  // Fetch TraceLink serial number hierarchy levels
  useEffect(() => {
    if (!validationData?.materialNumber || !validationData?.vendorBatchId) return;
    let cancelled = false;

    const fetchHierarchyLevels = async () => {
      setHierarchyLoading(true);
      setHierarchyError(null);
      try {
        const result = await apiClient.getTraceLinkHierarchyLevels(
          validationData.materialNumber!,
          validationData.vendorBatchId!
        );
        if (cancelled) return;
        if (result.found && result.levels) {
          setHierarchyLevels(result.levels);
        } else {
          setHierarchyLevels(null);
        }
      } catch (err) {
        if (cancelled) return;
        let message = 'Failed to fetch hierarchy data';
        if (err instanceof Error) {
          const jsonMatch = err.message.match(/:\s*(\{.*\})\s*$/);
          if (jsonMatch) {
            try {
              const parsed = JSON.parse(jsonMatch[1]);
              message = parsed.error || parsed.message || err.message;
            } catch { message = err.message; }
          } else {
            message = err.message;
          }
        }
        setHierarchyError(message);
      } finally {
        if (!cancelled) setHierarchyLoading(false);
      }
    };

    fetchHierarchyLevels();
    return () => { cancelled = true; };
  }, [validationData?.materialNumber, validationData?.vendorBatchId, apiClient]);

  /** Render an editable cell with all required props */
  const openHighlightModal = (row: ValidationRow, blobName: string) => {
    if (!row.highlight || row.highlight.length === 0 || !blobName) return;
    const hl = row.highlight[0];
    setHighlightModal({
      blobName,
      highlightPage: hl.page,
      highlightRects: hl.rects,
      matchType: hl.match_type,
      fieldName: row.fieldName,
      matchedText: hl.matched_text,
    });
  };

  const renderHighlightIcon = (row: ValidationRow, blobName: string | undefined) => {
    if (!row.highlight || row.highlight.length === 0 || !blobName) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openHighlightModal(row, blobName); }}
        className="ml-1 p-0.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
        title="View in document"
      >
        <Eye size={14} />
      </button>
    );
  };

  const renderEditableCell = (tableKey: string, extractionKey: string, originalExtracted: string, className?: string, cellWidth?: string) => {
    const id = cellId(tableKey, extractionKey);
    return (
      <EditableValueCell
        tableKey={tableKey}
        extractionKey={extractionKey}
        originalExtracted={originalExtracted}
        isEditing={editingCell === id}
        displayValue={getDisplayValue(tableKey, extractionKey, originalExtracted)}
        isOverridden={isOverridden(tableKey, extractionKey)}
        onStartEdit={() => setEditingCell(id)}
        onEdit={(newValue) => handleEdit(tableKey, extractionKey, newValue)}
        onStopEdit={() => handleStopEdit(tableKey, extractionKey)}
        className={className}
        cellWidth={cellWidth}
        hasError={invalidDateFields.has(id)}
      />
    );
  };

  // Reusable collapsible table header component
  const CollapsibleTableHeader: React.FC<{
    tableId: string;
    title: string;
    filename: string;
    isExpanded: boolean;
    onToggle: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
  }> = ({ tableId, title, filename, isExpanded, onToggle, onDelete, isDeleting }) => {
    const isEmptyTable = filename === NO_DOCUMENT_FILENAME;

    return (
      <div
        className={`p-4 border-b cursor-pointer hover:bg-gray-100 transition-colors ${
          isEmptyTable ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'
        }`}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900 break-words">{title}</h2>
            </div>
            <p
              className={`text-xs mt-1 flex items-center gap-2 break-words ${
                isEmptyTable ? 'text-orange-600 font-medium' : 'text-gray-500'
              }`}
            >
              {isEmptyTable ? (
                <>
                  <AlertTriangle size={12} className="flex-shrink-0" />
                  <span className="break-all">{filename} - Manual entry required</span>
                </>
              ) : (
                <>
                  <FileText size={12} className="flex-shrink-0" />
                  <span className="break-all">{filename}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                disabled={isDeleting}
                className="p-1.5 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                title="Delete document"
                data-testid={`delete-document-${tableId}`}
              >
                {isDeleting ? (
                  <Loader2 size={16} className="text-gray-400 animate-spin" />
                ) : (
                  <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
                )}
              </button>
            )}
            <ChevronDown
              size={20}
              className={`text-gray-600 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>
      </div>
    );
  };

  // Reusable validation table component
  const ValidationTable = ({
    tableId,
    title,
    filename,
    blobName,
    data,
    onDelete,
    isDeleting,
  }: {
    tableId: string;
    title: string;
    filename: string;
    blobName?: string;
    data: ValidationRow[];
    onDelete?: () => void;
    isDeleting?: boolean;
  }) => {
    const isEmptyTable = filename === NO_DOCUMENT_FILENAME;
    const isExpanded = expandedTables.includes(tableId);

    return (
      <div className={`bg-white rounded-lg border shadow-sm overflow-hidden mt-6 ${
        isEmptyTable ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'
      }`}>
        <CollapsibleTableHeader
          tableId={tableId}
          title={title}
          filename={filename}
          isExpanded={isExpanded}
          onToggle={() => toggleTable(tableId)}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />

        {isExpanded && (
        <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className="w-full" style={{ tableLayout: 'fixed', maxWidth: '100%' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '15%' }}>
                  Field Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '25%' }}>
                  Extracted Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '25%' }}>
                  Validation Source 1
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '30%' }}>
                  Validation Source 2
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4" style={{ width: '15%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '15%', minWidth: '0' }}>
                    <div className="text-sm font-medium text-gray-900 flex items-center justify-between gap-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      <span className="flex-1">{row.fieldName}</span>
                      <span className="flex-shrink-0">{renderHighlightIcon(row, blobName)}</span>
                    </div>
                    <div className="mt-1"><OcrConfidenceBadge score={row.ocrConfidence} /></div>
                  </td>
                  {renderEditableCell(tableId, row.extractionKey, row.extractedValue, "px-6 py-4", "25%")}
                  <td
                    className={`px-6 py-4${row.validationSource1?.value ? ' cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                    style={{ width: '30%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '30%', minWidth: '0' }}
                    onClick={() => row.validationSource1?.value && handleEdit(tableId, row.extractionKey, row.validationSource1.value)}
                    title={row.validationSource1?.value ? 'Click to use this value' : undefined}
                  >
                    {row.validationSource1 && row.validationSource1.value ? (
                      <>
                        <div className="text-sm text-gray-900" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{row.validationSource1.value}</div>
                        {row.validationSource1.source && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                            <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                            <span style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>Source: {row.validationSource1.source}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">{'\u2014'}</div>
                    )}
                  </td>
                  <td
                    className={`px-6 py-4${row.validationSource2?.value ? ' cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                    style={{ width: '30%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '30%', minWidth: '0' }}
                    onClick={() => row.validationSource2?.value && handleEdit(tableId, row.extractionKey, row.validationSource2.value)}
                    title={row.validationSource2?.value ? 'Click to use this value' : undefined}
                  >
                    {row.validationSource2 && row.validationSource2.value ? (
                      <>
                        <div className="text-sm text-gray-900" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{row.validationSource2.value}</div>
                        {row.validationSource2.source && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                            <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                            <span style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>Source: {row.validationSource2.source}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">{'\u2014'}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };

  // Three-source validation table component for documents with three validation sources
  const ThreeSourceValidationTable = ({
    tableId,
    title,
    filename,
    blobName,
    data,
    onDelete,
    isDeleting,
  }: {
    tableId: string;
    title: string;
    filename: string;
    blobName?: string;
    data: ValidationRow[];
    onDelete?: () => void;
    isDeleting?: boolean;
  }) => {
    const isEmptyTable = filename === NO_DOCUMENT_FILENAME;
    const isExpanded = expandedTables.includes(tableId);

    return (
      <div className={`bg-white rounded-lg border shadow-sm overflow-hidden mt-6 ${
        isEmptyTable ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'
      }`}>
        <CollapsibleTableHeader
          tableId={tableId}
          title={title}
          filename={filename}
          isExpanded={isExpanded}
          onToggle={() => toggleTable(tableId)}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />

        {isExpanded && (
        <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className="w-full" style={{ tableLayout: 'fixed', maxWidth: '100%' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '16%' }}>
                  Field Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '21%' }}>
                  Extracted Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '21%' }}>
                  Validation 1
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '21%' }}>
                  Validation 2
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '21%' }}>
                  Validation 3
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4" style={{ width: '16%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '16%', minWidth: '0' }}>
                    <div className="text-sm font-medium text-gray-900 flex items-center justify-between gap-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      <span className="flex-1 flex items-center">
                        {row.fieldName}
                        {(tableId === 'DELIVERY_NOTE' || tableId.startsWith('DELIVERY_NOTE_')) && row.extractionKey === 'destination_country' && (
                          <TransportTypeBadge batchType={batchType} />
                        )}
                      </span>
                      <span className="flex-shrink-0">{renderHighlightIcon(row, blobName)}</span>
                    </div>
                    <div className="mt-1"><OcrConfidenceBadge score={row.ocrConfidence} /></div>
                  </td>
                  {renderEditableCell(tableId, row.extractionKey, row.extractedValue, "px-6 py-4", "21%")}
                  <td
                    className={`px-6 py-4${row.validationSource1?.value ? ' cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                    style={{ width: '21%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '21%', minWidth: '0' }}
                    onClick={() => row.validationSource1?.value && handleEdit(tableId, row.extractionKey, row.validationSource1.value)}
                    title={row.validationSource1?.value ? 'Click to use this value' : undefined}
                  >
                    {row.validationSource1 && row.validationSource1.value ? (
                      <>
                        <div className="text-sm text-gray-900" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{row.validationSource1.value}</div>
                        {row.validationSource1.source && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                            <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></span>
                            <span style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>Source: {row.validationSource1.source}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">{'\u2014'}</div>
                    )}
                  </td>
                  <td
                    className={`px-6 py-4${row.validationSource2?.value ? ' cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                    style={{ width: '21%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '21%', minWidth: '0' }}
                    onClick={() => row.validationSource2?.value && handleEdit(tableId, row.extractionKey, row.validationSource2.value)}
                    title={row.validationSource2?.value ? 'Click to use this value' : undefined}
                  >
                    {row.validationSource2 && row.validationSource2.value ? (
                      <>
                        <div className="text-sm text-gray-900" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{row.validationSource2.value}</div>
                        {row.validationSource2.source && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                            <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                            <span style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>Source: {row.validationSource2.source}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">{'\u2014'}</div>
                    )}
                  </td>
                  <td
                    className={`px-6 py-4${row.validationSource3?.value ? ' cursor-pointer hover:bg-blue-50 transition-colors' : ''}`}
                    style={{ width: '21%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '21%', minWidth: '0' }}
                    onClick={() => row.validationSource3?.value && handleEdit(tableId, row.extractionKey, row.validationSource3.value)}
                    title={row.validationSource3?.value ? 'Click to use this value' : undefined}
                  >
                    {row.validationSource3 && row.validationSource3.value ? (
                      <>
                        <div className="text-sm text-gray-900" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{row.validationSource3.value}</div>
                        {row.validationSource3.source && (
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
                            <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                            <span style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>Source: {row.validationSource3.source}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-400 italic">{'\u2014'}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };

  // Simplified table component for documents without validation columns
  const SimpleExtractionTable = ({
    tableId,
    title,
    filename,
    blobName,
    data,
    onDelete,
    isDeleting,
  }: {
    tableId: string;
    title: string;
    filename: string;
    blobName?: string;
    data: ValidationRow[];
    onDelete?: () => void;
    isDeleting?: boolean;
  }) => {
    const isEmptyTable = filename === NO_DOCUMENT_FILENAME;
    const isExpanded = expandedTables.includes(tableId);

    return (
      <div className={`bg-white rounded-lg border shadow-sm overflow-hidden mt-6 ${
        isEmptyTable ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200'
      }`}>
        <CollapsibleTableHeader
          tableId={tableId}
          title={title}
          filename={filename}
          isExpanded={isExpanded}
          onToggle={() => toggleTable(tableId)}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />

        {isExpanded && (
        <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className="w-full" style={{ tableLayout: 'fixed', maxWidth: '100%' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '30%' }}>
                  Field Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" style={{ width: '70%' }}>
                  Extracted Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4" style={{ width: '30%', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '30%', minWidth: '0' }}>
                    <div className="text-sm font-medium text-gray-900 flex items-center justify-between gap-1" style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                      <span className="flex-1">{row.fieldName}</span>
                      <span className="flex-shrink-0">{renderHighlightIcon(row, blobName)}</span>
                    </div>
                    <div className="mt-1"><OcrConfidenceBadge score={row.ocrConfidence} /></div>
                  </td>
                  {renderEditableCell(tableId, row.extractionKey, row.extractedValue, "px-6 py-4", "70%")}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    );
  };

  /**
   * For a packaging sub-type table, find the matching artwork from lotInfoData
   * and enrich rows that have no validation source with TIBCO artwork values.
   */
  const enrichRowsWithArtwork = (rows: ValidationRow[], tableKey: string): ValidationRow[] => {
    const artworkLabel = PACKAGING_SUBTYPE_TO_ARTWORK_LABEL[tableKey];
    if (!artworkLabel || !lotInfoData?.artworks?.length) return rows;

    const matchingArtwork = lotInfoData.artworks.find(
      a => a.materialGroupLabel === artworkLabel
    );
    if (!matchingArtwork) return rows;

    const artworkSourceMap: Record<string, string | null> = {
      'Artworks Material Number': matchingArtwork.materialId,
      'Artworks Version': matchingArtwork.version,
      'Packaging Type': matchingArtwork.materialGroupLabel,
    };

    return rows.map(row => {
      if (row.validationSource1) return row;
      const artworkValue = artworkSourceMap[row.fieldName];
      if (artworkValue == null) return row;
      return { ...row, validationSource1: { value: String(artworkValue), source: 'TIBCO Artworks' } };
    });
  };

  /**
   * For batch quantity fields, append the SAP unit of measure to validation sources.
   * When multiple lots are selected, replace the SAP source with per-lot breakdown.
   */
  const enrichRowsWithSapLotQuantities = (rows: ValidationRow[]): ValidationRow[] => {
    const quantityFields = ['quantity', 'fp_batch_quantity'];
    const unit = String(formData?.sapIdentityBatch?.unitOfMeasure?.value || '');

    if (!unit && (!hasMultipleLots || !sapLotQuantities || sapLotQuantities.length <= 1)) return rows;

    return rows.map(row => {
      if (!quantityFields.includes(row.extractionKey)) return row;

      if (hasMultipleLots && sapLotQuantities && sapLotQuantities.length > 1) {
        const perLotValues = sapLotQuantities.map((lq: { lotId: string; quantity: number | null }) => {
          const qty = lq.quantity ?? 0;
          const formattedQty = qty.toLocaleString();
          return unit ? `Lot ${lq.lotId}: ${formattedQty} ${unit}` : `Lot ${lq.lotId}: ${formattedQty}`;
        }).join(' / ');

        const totalQuantity = sapLotQuantities.reduce((sum: number, lq: { lotId: string; quantity: number | null }) => sum + (lq.quantity ?? 0), 0);
        const formattedTotal = totalQuantity.toLocaleString();
        const totalWithUnit = unit ? `${formattedTotal} ${unit}` : formattedTotal;

        return {
          ...row,
          validationSource1: {
            value: `Total: ${totalWithUnit}`,
            source: perLotValues,
          },
        };
      }

      // Single lot: append unit to existing SAP source value
      if (unit && row.validationSource1 && row.validationSource1.value) {
        const val = row.validationSource1.value;
        if (!val.includes(unit)) {
          return {
            ...row,
            validationSource1: {
              ...row.validationSource1,
              value: `${val} ${unit}`,
            },
          };
        }
      }

      return row;
    });
  };

  /** Render a table for a given document type key */
  const renderTable = (tableKey: string) => {
    // Extract base type from indexed keys (e.g., DELIVERY_NOTE_0 -> DELIVERY_NOTE)
    // and compound sub-type keys (e.g., PACKAGING_MATERIALS__LABEL -> PACKAGING_MATERIALS)
    const indexedMatch = tableKey.match(/^(.+)_(\d+)$/);
    const baseType = (indexedMatch ? indexedMatch[1] : tableKey).replace(/__[A-Z_]+$/, '');

    // Try indexed key first, then fall back to base key for backward compatibility
    const tableData = validationData?.tables[tableKey] || validationData?.tables[baseType];

    // Determine table title and filename
    // If API provides data, use it; otherwise use defaults
    const title = tableData?.title || getDocumentTypeTitle(tableKey, documentCounts) || tableKey;
    const filename = tableData?.filename || NO_DOCUMENT_FILENAME;
    const blobName = tableData?.blob_name || undefined;

    // Get rows: if API provides data use it, otherwise show empty rows based on API schema.
    // Enrich packaging material rows with artwork validation sources from inspection lot info.
    // Enrich batch quantity rows with per-lot SAP quantities when multiple lots are selected.
    const baseRows = tableData ? toValidationRows(tableData.rows) : generateEmptyRows(baseType);
    const artworkEnrichedRows = enrichRowsWithArtwork(baseRows, tableKey);
    const rows = enrichRowsWithSapLotQuantities(artworkEnrichedRows);

    const handleDelete = () => handleDeleteDocument(tableKey, blobName);
    const isCurrentlyDeleting = deletingDocument === tableKey;

    if (THREE_SOURCE_TABLES.has(baseType)) {
      return (
        <ThreeSourceValidationTable
          key={tableKey}
          tableId={tableKey}
          title={title}
          filename={filename}
          blobName={blobName}
          data={rows}
          onDelete={handleDelete}
          isDeleting={isCurrentlyDeleting}
        />
      );
    }

    if (EXTRACTION_ONLY_TABLES.has(baseType)) {
      return (
        <SimpleExtractionTable
          key={tableKey}
          tableId={tableKey}
          title={title}
          filename={filename}
          blobName={blobName}
          data={rows}
          onDelete={handleDelete}
          isDeleting={isCurrentlyDeleting}
        />
      );
    }

    return (
      <ValidationTable
        key={tableKey}
        tableId={tableKey}
        title={title}
        filename={filename}
        blobName={blobName}
        data={rows}
        onDelete={handleDelete}
        isDeleting={isCurrentlyDeleting}
      />
    );
  };

  // State for graph write errors
  const [graphWriteError, setGraphWriteError] = useState<string | null>(null);

  /**
   * Determine the source of a field value based on validation row and user overrides
   * Priority: Manual override > Document extraction > Validation Source 1 > Validation Source 2
   */
  const determineSource = (row: ValidationRow, overrideValue: string | undefined): FieldSource => {
    // If user manually edited, it's Manual
    if (overrideValue !== undefined) {
      return 'Manual';
    }

    // If extracted from document
    if (row.extractedValue) {
      return 'Document';
    }

    // Use validation source 1 if available
    if (row.validationSource1?.value && row.validationSource1?.source) {
      return row.validationSource1.source as FieldSource;
    }

    // Use validation source 2 if available
    if (row.validationSource2?.value && row.validationSource2?.source) {
      return row.validationSource2.source as FieldSource;
    }

    // Use validation source 3 if available (for three-source tables like Delivery Note)
    if (row.validationSource3?.value && row.validationSource3?.source) {
      return row.validationSource3.source as FieldSource;
    }

    // No source found
    return 'Error';
  };

  const handleProceed = async () => {
    if (!fieldsConfirmed || saving || invalidDateFields.size > 0) return;

    setSaving(true);
    setGraphWriteError(null);
    blockNavigation?.('Saving Document Validation...');
    try {
      const userName = sanitizeEmailForFolderName(userEmail);

      // 0. Save document validation state to Neo4j with validatedAt timestamp
      const pageData: DocumentValidationData = {
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined,
        fieldsConfirmed,
        serializationRequirement: serializationRequirement || undefined,
        aggregationRequirement: aggregationRequirement || undefined,
        validatedAt: new Date().toISOString(),
        manualSections: manualSections.length > 0 ? manualSections : undefined,
        syncedResults: Object.keys(syncedData).length > 0 ? syncedData : undefined,
        tablesSnapshot: validationData ?? undefined,
        deletedDocuments: deletedDocuments.size > 0 ? Array.from(deletedDocuments) : undefined,
        // TraceLink lot status data for Word export
        tracelinkGtin: traceLinkData?.['GTIN'] || undefined,
        tracelinkQuantity: traceLinkData?.['Quantity'] || undefined,
        tracelinkHierarchyLevels: hierarchyLevels || undefined,
      };

      await apiClient.saveReview({
        batchId: batchId,
        sessionId,
        pageName: 'document_validation',
        pageData,
        batchType,
        status: 'draft',
      });

      // Update initial state to prevent unsaved changes prompt on navigation
      initialStateRef.current = {
        overrides: JSON.parse(JSON.stringify(overrides)),
        fieldsConfirmed,
        serializationRequirement,
        aggregationRequirement,
      };

      // Update session metadata
      try {
        await sessionClient.updateSession(sessionId, {
          batchId: batchId,
          currentPage: 'document-validation',
          lastSavedAt: new Date(),
          status: SessionStatus.InProgress,
          details: JSON.stringify({
            inspectionLotId: inspectionLotId ?? null,
            productName: productName ?? null,
            batchType: batchType ?? 'direct',
            priority: priority ?? 'medium',
            selectedDocuments: selectedDocuments,
            brrLevel: brrLevel ?? 'na',
            documentCounts: documentCounts,
            expectedReleaseDate: expectedReleaseDate ?? null,
            delayReason: delayReason ?? null,
            delayDetails: delayDetails ?? null,
            delayActions: delayActions ?? null,
            delayCmoName: delayCmoName ?? null,
            inProgressAt: inProgressAt ?? null,
            docCompletenessAt: new Date().toISOString(),
          }),
        });
      } catch (sessionError) {
        console.error('[DocumentValidation] session updateSession FAILED:', sessionError);
      }

      // 1. Build validated state from ALL validation tables for persistence
      const validatedFields: Record<string, import('../services/types').FieldValue> = {};

      if (validationData?.tables) {
        Object.entries(validationData.tables).forEach(([tableKey, tableData]) => {
          if (!tableData || !tableData.rows) return;

          const rows = toValidationRows(tableData.rows);
          rows.forEach(row => {
            const fieldPath = `${tableKey}.${row.extractionKey}`;
            const finalValue = overrides[tableKey]?.[row.extractionKey] ?? row.extractedValue;
            const source = determineSource(row, overrides[tableKey]?.[row.extractionKey]);

            validatedFields[fieldPath] = {
              value: finalValue,
              source: source,
              override: !!overrides[tableKey]?.[row.extractionKey]
            };
          });
        });
      }

      // 2. Save validated state to the session store
      updateFields(validatedFields);
      await saveValidatedState();

      // 3. Save overrides to blob (if any) - keep existing behavior
      // Use session-scoped blob prefix to match where orchestrator stored results
      const blobPrefix = sessionId ? `${userName}/${sessionId}` : userName;
      if (hasOverrides) {
        await apiClient.saveValidationOverrides(sessionId, overrides);
      }

      // 4. Write validated extractions to graph with session scoping
      // sessionId ensures documents are tagged for this specific BRR session
      await apiClient.writeToGraph(blobPrefix, batchId, productName, sessionId);

      // 5. Extract validated field values (with user overrides applied) and navigate to Batch Record Review
      const extractedCountry = extractCountryFromValidation(validationData, overrides);
      const extractedProductName = extractProductNameFromValidation(validationData, overrides);
      const extractedInspectionLot = extractInspectionLotFromValidation(validationData, overrides);
      const extractedManufactureDate = extractManufactureDate(validationData, overrides);
      const extractedExpiryDate = extractExpiryDate(validationData, overrides);
      // Unblock navigation before transitioning to next page
      unblockNavigation?.();
      onNext(
        extractedCountry,
        extractedProductName,
        extractedInspectionLot,
        extractedManufactureDate,
        extractedExpiryDate
      );
    } catch (err) {
      // If save or graph write fails, don't navigate — user can retry
      const raw = err instanceof Error ? err.message : 'Failed to write to graph';
      const message = /validation error|invalid.*date|date_from_datetime/i.test(raw)
        ? 'One or more date fields contain an invalid value. Please check that all dates use the DD.MM.YYYY, MM.YYYY, or YYYY format and try again.'
        : `Failed to save. ${raw}`;
      setGraphWriteError(message);
      setSaving(false);
      unblockNavigation?.(message);
    }
  };

  return (
    <div className="bg-gray-50" style={{ boxSizing: 'border-box' }}>
      <style>{`
        .validation-page-root, .validation-page-root *, .validation-page-root *::before, .validation-page-root *::after {
          box-sizing: border-box;
        }
      `}</style>
      <div className="validation-page-root w-full p-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2 break-words">Document Validation</h1>
          <p className="text-sm text-gray-600 break-words">
            Validate extracted data against multiple sources to ensure accuracy before proceeding to Batch Record Review
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="loading-spinner">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={32} className="animate-spin text-green-600" />
              <p className="text-sm text-gray-600">Loading validation data...</p>
            </div>
          </div>
        )}

        {/* No Results State — documents not yet uploaded/processed */}
        {noResults && !loading && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 mb-6" data-testid="no-results">
            <div className="flex flex-col items-center text-center">
              <Upload size={48} className="text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-500 mb-2">No extraction results available</h3>
              <p className="text-sm text-gray-400 max-w-md">
                Documents have not been uploaded and processed yet. Please go back to upload your documents and run the extraction pipeline first.
              </p>
              <button
                onClick={onBack}
                className="mt-6 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
              >
                Go to Document Upload
              </button>
            </div>
          </div>
        )}

        {/* Validation Tables — only render when data is loaded */}
        {!loading && !noResults && validationData && (
          <>

            {extractionFailed && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg" data-testid="extraction-failed">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      Extraction Failed
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p>
                        Your documents were uploaded, but no data could be extracted from them.
                      </p>
                      <p className="mt-1">
                        You can enter the required information manually using the "Add Document Section" dropdown below, or go back to the Start page to re-upload the documents.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deleted Required Documents Warning Banner */}
            {deletedDocuments.size > 0 && (() => {
              const deletedRequired = Array.from(deletedDocuments).filter(tableKey => {
                const match = tableKey.match(/^(.+)_(\d+)$/);
                const baseType = match ? match[1] : tableKey;
                return REQUIRED_DOCUMENT_TYPES.has(baseType);
              });
              if (deletedRequired.length === 0) return null;
              return (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-r-lg" data-testid="deleted-documents-warning">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Required Documents Deleted
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          The following required documents have been deleted:{' '}
                          <strong>
                            {deletedRequired
                              .map(tableKey => {
                                const match = tableKey.match(/^(.+)_(\d+)$/);
                                const baseType = match ? match[1] : tableKey;
                                return DOCUMENT_TYPE_TITLES[baseType] || baseType;
                              })
                              .filter((v, i, a) => a.indexOf(v) === i)
                              .join(', ')}
                          </strong>
                        </p>
                        <p className="mt-1">
                          You can re-add these documents using the "Add Document Section" dropdown below, or go back to the Start page to upload new documents.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {(() => {
              const visibleKeys = buildTableDisplayOrder(documentCounts)
                .filter(tableKey => {
                  if (deletedDocuments.has(tableKey)) return false;
                  const indexedMatch = tableKey.match(/^(.+)_(\d+)$/);
                  if (indexedMatch) {
                    const baseType = indexedMatch[1];
                    const docId = Object.entries(DOCUMENT_ID_TO_TABLE_KEY)
                      .find(([, value]) => value === baseType)?.[0];
                    return !docId || selectedDocuments.length === 0 || selectedDocuments.includes(docId);
                  }
                  const docId = Object.entries(DOCUMENT_ID_TO_TABLE_KEY)
                    .find(([, value]) => value === tableKey)?.[0];
                  return !docId || selectedDocuments.length === 0 || selectedDocuments.includes(docId);
                });

              // Group tables that share the same source_file (combined documents)
              const sourceFileMap = new Map<string, string[]>();
              for (const key of visibleKeys) {
                const tableData = validationData?.tables[key];
                const sf = tableData?.source_file;
                if (sf) {
                  const existing = sourceFileMap.get(sf) || [];
                  existing.push(key);
                  sourceFileMap.set(sf, existing);
                }
              }
              const groupedKeys = new Set<string>();
              sourceFileMap.forEach((keys) => {
                if (keys.length >= 2) keys.forEach(k => groupedKeys.add(k));
              });

              const rendered: React.ReactNode[] = [];
              const renderedGroups = new Set<string>();

              for (const tableKey of visibleKeys) {
                if (!groupedKeys.has(tableKey)) {
                  rendered.push(renderTable(tableKey));
                  continue;
                }
                // Find which source_file group this key belongs to
                const sf = validationData?.tables[tableKey]?.source_file;
                if (!sf || renderedGroups.has(sf)) continue;
                renderedGroups.add(sf);

                const groupKeys = sourceFileMap.get(sf)!;
                rendered.push(
                  <div key={`combined-${sf}`} className="mt-6 border border-indigo-200 rounded-lg bg-indigo-50/20" data-testid="combined-document-group">
                    {groupKeys.map(k => renderTable(k))}
                  </div>
                );
              }

              return rendered;
            })()}

            {/* Manual Document Sections */}
            {manualSections.map(section => {
              const baseDocType = section.documentType;
              const title = `${DOCUMENT_TYPE_TITLES[baseDocType] || baseDocType} (Manual)`;
              const synced = syncedData[section.tableKey];
              const rows: ValidationRow[] = synced
                ? toValidationRows(synced)
                : generateEmptyRows(baseDocType);
              const isSyncing = syncingSection === section.id;
              const isExpanded = expandedTables.includes(section.id);

              return (
                <div key={section.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-6" data-testid={`manual-section-${section.id}`}>
                  <div
                    className="p-4 border-b border-gray-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => toggleTable(section.id)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 break-words">{title}</h2>
                        <p className="text-xs text-gray-500 mt-1 break-words">
                          {section.syncStatus === 'synced' ? 'Validation sources populated' : 'Fill in fields and click Validate Sources to populate validation sources'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setManualSections(prev => prev.filter(s => s.id !== section.id));
                            setSyncedData(prev => {
                              const next = { ...prev };
                              delete next[section.tableKey];
                              return next;
                            });
                          }}
                          className="p-1.5 hover:bg-red-100 rounded transition-colors"
                          title="Remove manual section"
                          data-testid={`remove-manual-${section.id}`}
                        >
                          <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
                        </button>
                        <ChevronDown
                          size={20}
                          className={`text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed', width: '100%' }}>
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>Field</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Value</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Validation Source 1</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>Validation Source 2</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {rows.map((row) => {
                              const cellId = `${section.tableKey}:${row.extractionKey}`;
                              const overrideValue = overrides[section.tableKey]?.[row.extractionKey];
                              const displayValue = overrideValue !== undefined ? overrideValue : row.extractedValue;
                              const isEditing = editingCell === cellId;
                              const isOverridden = overrideValue !== undefined;
                              return (
                                <tr key={row.extractionKey} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900" style={{ width: '20%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {row.fieldName}
                                  </td>
                                  <EditableValueCell
                                    tableKey={section.tableKey}
                                    extractionKey={row.extractionKey}
                                    originalExtracted={row.originalValue}
                                    isEditing={isEditing}
                                    displayValue={displayValue}
                                    isOverridden={isOverridden}
                                    onStartEdit={() => setEditingCell(cellId)}
                                    onEdit={(newValue) => {
                                      setOverrides(prev => ({
                                        ...prev,
                                        [section.tableKey]: {
                                          ...(prev[section.tableKey] || {}),
                                          [row.extractionKey]: newValue,
                                        }
                                      }));
                                    }}
                                    onStopEdit={() => setEditingCell(null)}
                                    cellWidth="25%"
                                  />
                                  <td className="px-6 py-4 text-sm text-gray-500" style={{ width: '25%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {row.validationSource1 ? (
                                      <div>
                                        <span>{row.validationSource1.value || '—'}</span>
                                        <div className="text-xs text-gray-400">{row.validationSource1.source}</div>
                                      </div>
                                    ) : '—'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500" style={{ width: '25%', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {row.validationSource2 ? (
                                      <div>
                                        <span>{row.validationSource2.value || '—'}</span>
                                        <div className="text-xs text-gray-400">{row.validationSource2.source}</div>
                                      </div>
                                    ) : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center gap-3">
                        <button
                          onClick={async () => {
                            setSyncingSection(section.id);
                            try {
                              const fieldValues = overrides[section.tableKey] || {};
                              const result = await apiClient.syncManualSection(
                                section.documentType,
                                fieldValues,
                                inspectionLotId,
                                productName,
                              );
                              if (result.status === 'ok' && result.rows) {
                                setSyncedData(prev => ({ ...prev, [section.tableKey]: result.rows }));
                                setManualSections(prev => prev.map(s =>
                                  s.id === section.id ? { ...s, syncStatus: 'synced' } : s
                                ));
                              }
                            } catch (err) {
                              console.error('Sync failed:', err);
                              setManualSections(prev => prev.map(s =>
                                s.id === section.id ? { ...s, syncStatus: 'error' } : s
                              ));
                            } finally {
                              setSyncingSection(null);
                            }
                          }}
                          disabled={isSyncing}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                          data-testid={`sync-btn-${section.id}`}
                        >
                          {isSyncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                          {isSyncing ? 'Validating...' : 'Validate Sources'}
                        </button>
                        {section.syncStatus === 'synced' && (
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle size={14} /> Validated
                          </span>
                        )}
                        {section.syncStatus === 'error' && (
                          <span className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle size={14} /> Validation failed
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add Document Section Button */}
            <div className="mt-6 flex items-center gap-3" data-testid="add-document-section">
              <button
                onClick={() => setShowAddDocDropdown(!showAddDocDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                data-testid="add-document-btn"
              >
                <Plus size={16} />
                Add Document Section
              </button>
              {showAddDocDropdown && (
                <select
                  autoFocus
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const docType = e.target.value;
                    const timestamp = Date.now();
                    const newSection: ManualSectionEntry = {
                      id: `MANUAL_${docType}_${timestamp}`,
                      tableKey: `MANUAL_${docType}_${timestamp}`,
                      documentType: docType,
                    };
                    setManualSections(prev => [...prev, newSection]);
                    setExpandedTables(prev => [...prev, newSection.id]);
                    setShowAddDocDropdown(false);
                  }}
                  onBlur={() => setShowAddDocDropdown(false)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm bg-white"
                  data-testid="add-document-select"
                >
                  <option value="">Select document type...</option>
                  {Object.entries(DOCUMENT_TYPE_TITLES).map(([key, title]) => (
                    <option key={key} value={key}>{title}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Serialization Validation Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-6">
              <div
                className="p-4 border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleTable('serialization')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 break-words">Serialization Report</h2>
                    <p className="text-xs text-gray-500 mt-1 break-words">TraceLink Lot Status</p>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-gray-600 transition-transform flex-shrink-0 ${expandedTables.includes('serialization') ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {expandedTables.includes('serialization') && (
                <div>
                  {/* TraceLink Lot Status Data */}
                  <div className="p-4 border-b border-gray-200">
                    {traceLinkLoading && (
                      <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="tracelink-loading">
                        <Loader2 size={16} className="animate-spin" />
                        <span>Fetching TraceLink lot status...</span>
                      </div>
                    )}
                    {traceLinkError && (
                      <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-md p-3" data-testid="tracelink-error">
                        <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                        <span>{traceLinkError}</span>
                      </div>
                    )}
                    {!traceLinkLoading && !traceLinkError && traceLinkFound === false && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-md p-3" data-testid="tracelink-not-found">
                        <AlertCircle size={16} className="text-gray-400 flex-shrink-0" />
                        <span>{traceLinkNotFoundMessage}</span>
                      </div>
                    )}
                    {!traceLinkLoading && !traceLinkError && !traceLinkData && !validationData?.materialNumber && (
                      <p className="text-sm text-gray-400">No material number available for TraceLink lookup.</p>
                    )}
                    {!traceLinkLoading && !traceLinkError && !traceLinkData && validationData?.materialNumber && !validationData?.vendorBatchId && (
                      <p className="text-sm text-gray-400">No vendor batch ID available for TraceLink lookup.</p>
                    )}
                    {!traceLinkLoading && !traceLinkError && traceLinkFound === true && traceLinkData && (() => {
                      const hiddenFields = new Set(['Success', 'Item Code', 'Lot Number', 'Internal Material Code']);
                      const dateFields = new Set(['Expiration Date', 'Manufacturing Date']);
                      const booleanValues: Record<string, boolean> = { 'true': true, 'false': false, 'yes': true, 'no': false };

                      const renderValue = (key: string, value: string) => {
                        if (dateFields.has(key)) {
                          return <span>{formatDate(value)}</span>;
                        }
                        const lower = value.toLowerCase();
                        if (lower in booleanValues) {
                          const isTrue = booleanValues[lower];
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isTrue ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {isTrue ? 'Yes' : 'No'}
                            </span>
                          );
                        }
                        return <span>{value || '—'}</span>;
                      };

                      const visibleEntries = Object.entries(traceLinkData).filter(([key]) => !hiddenFields.has(key));

                      return (
                        <div>
                          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-md p-3 mb-3" data-testid="tracelink-found">
                            <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                            <span>
                              TraceLink serialisation information found for Material Number: <strong>{validationData?.materialNumber}</strong> and Vendor Batch ID: <strong>{validationData?.vendorBatchId}</strong>
                            </span>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm table-fixed" data-testid="tracelink-lot-status-table">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Field</th>
                                  <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleEntries.map(([key, value]) => (
                                  <tr key={key} className="border-b border-gray-100">
                                    <td className="py-2 px-3 text-gray-600">{key}</td>
                                    <td className="py-2 px-3 text-gray-900 break-words">{renderValue(key, value)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Serial Number Hierarchy Levels */}
                  {!traceLinkLoading && !traceLinkError && traceLinkFound === true && (
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Serial Number Hierarchy</h3>
                      {hierarchyLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="hierarchy-loading">
                          <Loader2 size={16} className="animate-spin" />
                          <span>Fetching serial number hierarchy...</span>
                        </div>
                      )}
                      {hierarchyError && (
                        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-md p-3" data-testid="hierarchy-error">
                          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
                          <span>{hierarchyError}</span>
                        </div>
                      )}
                      {!hierarchyLoading && !hierarchyError && hierarchyLevels && hierarchyLevels.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm table-fixed" data-testid="hierarchy-levels-table">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Level</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {hierarchyLevels.map(({ level, quantity }) => (
                                <tr key={level} className="border-b border-gray-100">
                                  <td className="py-2 px-3 text-gray-600">{level}</td>
                                  <td className="py-2 px-3 text-gray-900">{quantity.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {!hierarchyLoading && !hierarchyError && (!hierarchyLevels || hierarchyLevels.length === 0) && (
                        <p className="text-sm text-gray-400">No hierarchy level data available.</p>
                      )}
                    </div>
                  )}

                  {/* Serialization Requirement Dropdown */}
                  <div className="p-4 bg-blue-50 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Serialization Requirement Status
                    </label>
                    <select
                      value={serializationRequirement}
                      onChange={(e) => setSerializationRequirement(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    >
                      <option value="">Select serialization requirement...</option>
                      <option value="yes-required-serialised">Yes, serialisation requirement in the country, goods are serialised</option>
                      <option value="no-requirement-not-serialised">No, no requirement in the destination country, goods are NOT serialised</option>
                      <option value="no-requirement-serialised-nonetheless">No, no requirement in the destination country, goods are serialised nonetheless</option>
                      <option value="na">N/A</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Aggregation Validation Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-6">
              <div
                className="p-4 border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleTable('aggregation')}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 break-words">Aggregation Report</h2>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`text-gray-600 transition-transform flex-shrink-0 ${expandedTables.includes('aggregation') ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {expandedTables.includes('aggregation') && (
                <div>
                  <div className="p-4 bg-blue-50 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Aggregation Requirement Status
                    </label>
                    <select
                      value={aggregationRequirement}
                      onChange={(e) => setAggregationRequirement(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    >
                      <option value="">Select aggregation requirement...</option>
                      <option value="yes-required-aggregated">Yes, aggregation requirement in the country, goods are aggregated</option>
                      <option value="no-requirement-not-aggregated">No, no requirement in the destination country, goods are NOT aggregated</option>
                      <option value="no-requirement-aggregated-nonetheless">No, no requirement in the destination country, goods are nevertheless aggregated</option>
                      <option value="na">N/A</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Confirmation Checkbox */}
            <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="fields-confirmation"
                  checked={fieldsConfirmed}
                  onChange={(e) => setFieldsConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 flex-shrink-0 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <label htmlFor="fields-confirmation" className="cursor-pointer flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 break-words">
                    I confirm that all validation fields have been reviewed and are correct
                  </div>
                  <div className="text-xs text-gray-500 mt-1 break-words">
                    You must confirm the accuracy of the validation data before proceeding to the Batch Record Review
                  </div>
                </label>
              </div>
            </div>

            {/* Graph write error banner */}
            {graphWriteError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="graph-write-error">
                <p className="text-sm text-red-700">
                  {graphWriteError}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex flex-col gap-4">
              {/* Save status and error */}
              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {saveError}
                </div>
              )}
              <div className="flex justify-end">
                <SaveStatusIndicator isSaving={loadingState} lastSaved={lastSaved} />
              </div>

              {/* Button row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBack}
                    disabled={isNavigationBlocked}
                    className={`px-4 py-2 rounded text-sm font-medium ${
                      isNavigationBlocked
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {'\u2190'} Back to Document Upload
                  </button>
                  {onDeleteBRR && <DeleteBRRButton onDelete={onDeleteBRR} />}
                </div>

                <div className="flex items-center gap-3">
                  {/* Save Progress button */}
                  <button
                    onClick={handleSaveProgress}
                    disabled={saving || loadingState}
                    data-testid="save-progress-button"
                    className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                      saving || loadingState
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    }`}
                  >
                    {saving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FileText size={16} />
                        Save Progress
                      </>
                    )}
                  </button>

                  {/* Proceed button */}
              <button
                onClick={handleProceed}
                disabled={!fieldsConfirmed || saving || invalidDateFields.size > 0 || isNavigationBlocked}
                data-testid="proceed-button"
                className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                  fieldsConfirmed && !saving && invalidDateFields.size === 0 && !isNavigationBlocked
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving || isNavigationBlocked ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {saving ? 'Saving...' : 'Please wait...'}
                  </>
                ) : (
                  <>
                    Proceed to Batch Record Review
                    <ChevronDown size={16} className="-rotate-90" />
                  </>
                )}
              </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* PDF Highlight Modal */}
      {highlightModal && (
        <PdfHighlightModal
          apiClient={apiClient}
          blobName={highlightModal.blobName}
          highlightPage={highlightModal.highlightPage}
          highlightRects={highlightModal.highlightRects}
          matchType={highlightModal.matchType}
          fieldName={highlightModal.fieldName}
          matchedText={highlightModal.matchedText}
          onClose={() => setHighlightModal(null)}
        />
      )}
    </div>
  );
};

export const DocumentValidationPage = React.memo(DocumentValidationPageComponent);
